import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getReschedulePolicyWindow,
  formatReschedulePolicyWindowStart,
  formatReschedulePolicyWindowRange,
} from './reschedulePolicyWindow';

export interface EnhancedRescheduleSlot {
  date: string;
  dayLabel: string;
  startTime: string;
  endTime: string;
  reserved: boolean;
  /** Whether this slot comes from a standard or reschedule-only schedule block. */
  scheduleType?: 'standard' | 'reschedule_only';
}

interface RescheduleMyStatsModalProps {
  open: boolean;
  onClose: () => void;
  availableMinutes: number;
  reservedMinutes: number;
  totalMinutes: number;
  slots: EnhancedRescheduleSlot[];
  onConfigureSchedules?: () => void;
}

function fmtH(m: number): string {
  const h = Math.floor(m / 60);
  const mins = m % 60;
  if (h > 0) return mins > 0 ? `${h}h ${mins}min` : `${h}h`;
  return `${mins}min`;
}

function fmtDate(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

const REQUIRED_MINUTES = 240; // 4 hours

export const RescheduleMyStatsModal: React.FC<RescheduleMyStatsModalProps> = ({
  open,
  onClose,
  availableMinutes,
  reservedMinutes,
  slots,
  onConfigureSchedules,
}) => {
  const missingMinutes = Math.max(0, REQUIRED_MINUTES - availableMinutes);
  const isOk = availableMinutes >= REQUIRED_MINUTES;

  const [slotsOpen, setSlotsOpen] = useState(false);
  useEffect(() => { if (open) setSlotsOpen(false); }, [open]);

  // Calculado en tiempo real: la ventana real de la política empieza 24hs
  // después del momento de la consulta, no a las 00:00 del día siguiente.
  const policyWindow = getReschedulePolicyWindow();

  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const periodEnd = new Date(tomorrow);
  periodEnd.setDate(periodEnd.getDate() + 6);
  const periodLabel = `${fmtDate(tomorrow)} – ${fmtDate(periodEnd)}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        data-testid="reschedule-mystats-modal"
        className="sm:max-w-[32rem] p-0 overflow-hidden"
      >
        {/* ── Header ── */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-[1.0625rem] font-semibold text-[#4a3d73]">
                Mis horarios de reprogramación
              </DialogTitle>
              <p className="text-[0.8125rem] text-gray-500 mt-1 leading-snug">
                <Tooltip delayDuration={150}>
                  <TooltipTrigger asChild>
                    <span
                      data-testid="reschedule-mystats-period-label"
                      className="underline decoration-dotted underline-offset-2 cursor-help"
                    >
                      Próximos 7 días
                    </span>
                  </TooltipTrigger>
                  <TooltipContent data-testid="reschedule-mystats-period-tooltip" side="bottom">
                    {formatReschedulePolicyWindowRange(policyWindow)}
                  </TooltipContent>
                </Tooltip>
                {' '}(desde {formatReschedulePolicyWindowStart(policyWindow)})
              </p>
            </div>
            <span
              data-testid="reschedule-mystats-status-badge"
              className={`shrink-0 mt-0.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                isOk
                  ? 'bg-[var(--lila-lighter)] text-[var(--lila-dark)]'
                  : 'bg-red-100 text-red-600'
              }`}
            >
              {isOk ? '✓ Suficientes' : '⚠ Insuficientes'}
            </span>
          </div>
        </DialogHeader>

        {/* ── Body ── */}
        <div className="px-6 py-5 space-y-5 overflow-y-auto max-h-[55vh]">
          {/* Metric cards */}
          <div className="grid grid-cols-3 gap-3">
            <MetricCard
              label="Libres"
              value={fmtH(availableMinutes)}
              testId="reschedule-mystats-available"
              color="green"
            />
            <MetricCard
              label="Agendadas"
              value={reservedMinutes > 0 ? fmtH(reservedMinutes) : '—'}
              testId="reschedule-mystats-reserved"
              color="gray"
            />
            <MetricCard
              label="Faltantes"
              value={missingMinutes > 0 ? fmtH(missingMinutes) : '—'}
              testId="reschedule-mystats-missing"
              color={missingMinutes > 0 ? 'red' : 'green'}
            />
          </div>

          {/* Slot list section */}
          <div>
            {slots.length === 0 ? (
              <>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  Detalle de horarios
                </p>
                <p
                  data-testid="reschedule-mystats-period"
                  className="text-[0.75rem] text-gray-400 mb-2"
                >
                  Período contemplado: {periodLabel}
                </p>
                <p
                  data-testid="reschedule-mystats-empty"
                  className="text-[0.8125rem] text-gray-400 py-3 text-center"
                >
                  No tenés franjas de reprogramación en los próximos 7 días.
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="reschedule-mystats-slots-toggle"
                  onClick={() => setSlotsOpen((v) => !v)}
                  className="flex items-center justify-between w-full text-left gap-2"
                >
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      Detalle de horarios
                    </p>
                    <p
                      data-testid="reschedule-mystats-period"
                      className="text-[0.75rem] text-gray-400 mt-0.5"
                    >
                      Período contemplado: {periodLabel}
                    </p>
                  </div>
                  {slotsOpen
                    ? <ChevronUp className="h-4 w-4 text-gray-400 shrink-0" />
                    : <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                  }
                </button>
                {slotsOpen && (
                  <ul
                    data-testid="reschedule-mystats-slots"
                    className="space-y-1.5 max-h-36 overflow-y-auto pr-1 mt-2"
                  >
                    {slots.map((slot, idx) => (
                      <li
                        key={`${slot.date}-${slot.startTime}-${idx}`}
                        className="flex items-center justify-between gap-2 py-1.5 px-3 bg-gray-50 rounded-lg text-[0.8125rem]"
                      >
                        <span className="text-gray-700">
                          <span className="font-medium">{slot.dayLabel}</span>
                          {' · '}
                          {slot.startTime}–{slot.endTime}
                        </span>
                        <span className="flex items-center gap-1.5 shrink-0">
                          <span
                            data-testid="reschedule-mystats-slot-type"
                            className={`px-1.5 py-0.5 rounded text-[0.6875rem] font-semibold ${
                              slot.scheduleType === 'reschedule_only'
                                ? 'bg-purple-100 text-purple-600'
                                : 'bg-blue-50 text-blue-600'
                            }`}
                          >
                            {slot.scheduleType === 'reschedule_only' ? 'Repr.' : 'Est.'}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              slot.reserved
                                ? 'bg-gray-200 text-gray-500'
                                : 'bg-green-100 text-green-700'
                            }`}
                          >
                            {slot.reserved ? 'Sesión agendada' : 'Libre'}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end items-center gap-3 flex-wrap">
          {missingMinutes > 0 ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer transition-colors"
              >
                Cerrar
              </button>
              <button
                type="button"
                data-testid="reschedule-mystats-configure-btn"
                onClick={() => {
                  onClose();
                  onConfigureSchedules?.();
                }}
                className="px-5 py-2 rounded-full text-sm font-semibold text-white border-none cursor-pointer transition-all hover:brightness-90"
                style={{ background: '#6D57BC' }}
              >
                Agregar horarios de reprogramación
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-full text-sm font-semibold text-white border-none cursor-pointer transition-all hover:brightness-90"
              style={{ background: '#6D57BC' }}
            >
              Entendido
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

type MetricColor = 'green' | 'gray' | 'red';

const MetricCard: React.FC<{
  label: string;
  value: string;
  testId: string;
  color: MetricColor;
}> = ({ label, value, testId, color }) => {
  const colorMap: Record<MetricColor, string> = {
    green: 'text-green-600',
    gray: 'text-gray-500',
    red: 'text-red-500',
  };
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center justify-center gap-0.5 p-3 rounded-xl bg-gray-50 border border-gray-100"
    >
      <span className={`text-lg font-bold leading-none ${colorMap[color]}`}>{value}</span>
      <span className="text-[0.6875rem] text-gray-400 text-center leading-tight mt-1">{label}</span>
    </div>
  );
};

export default RescheduleMyStatsModal;
